/**
 * Payment Management Hooks
 * Colaiste Lurgan Management System - Student payment data management
 */

import { useQuery, useMutation } from '@tanstack/react-query';
import { api } from '../lib/api';
import { queryKeys, invalidateQueries } from '../lib/queryClient';

/**
 * Get all payments for a specific student
 * @param {string} studentUuid - The student's UUID
 * @param {object} options - React Query options
 */
export function useStudentPayments(studentUuid, options = {}) {
  return useQuery({
    queryKey: queryKeys.payments.student(studentUuid),
    queryFn: async () => {
      const response = await api.payments.getByStudent(studentUuid);
      return response.results || response || [];
    },
    enabled: !!studentUuid,
    staleTime: 2 * 60 * 1000, // 2 minutes
    ...options,
  });
}

/**
 * Get all payments with optional filters
 * @param {object} filters - Filter parameters
 * @param {object} options - React Query options
 */
export function usePayments(filters = {}, options = {}) {
  return useQuery({
    queryKey: queryKeys.payments.list(filters),
    queryFn: () => api.payments.getAll(filters),
    staleTime: 2 * 60 * 1000, // 2 minutes
    ...options,
  });
}

/**
 * Get a single payment by UUID
 * @param {string} paymentUuid - The payment UUID
 * @param {object} options - React Query options
 */
export function usePayment(paymentUuid, options = {}) {
  return useQuery({
    queryKey: queryKeys.payments.detail(paymentUuid),
    queryFn: () => api.payments.getById(paymentUuid),
    enabled: !!paymentUuid,
    ...options,
  });
}

/**
 * Create a new payment
 * @param {object} options - Mutation options
 */
export function useCreatePayment(options = {}) {
  const { onSuccess: onSuccessOption, ...restOptions } = options;
  return useMutation({
    mutationFn: api.payments.create,
    onSuccess: (newPayment, variables, context) => {
      // Invalidate the student's payments and student detail
      if (newPayment.student) {
        invalidateQueries.studentPayments(newPayment.student);
        invalidateQueries.student(newPayment.student);
      }
      // Also invalidate general payments list
      invalidateQueries.payments();
      // Call the caller's onSuccess if provided
      onSuccessOption?.(newPayment, variables, context);
    },
    ...restOptions,
  });
}

/**
 * Update an existing payment
 * @param {object} options - Mutation options
 */
export function useUpdatePayment(options = {}) {
  const { onSuccess: onSuccessOption, ...restOptions } = options;
  return useMutation({
    mutationFn: ({ uuid, ...data }) => api.payments.update(uuid, data),
    onSuccess: (updatedPayment, variables, context) => {
      // Invalidate the student's payments and student detail
      if (updatedPayment.student) {
        invalidateQueries.studentPayments(updatedPayment.student);
        invalidateQueries.student(updatedPayment.student);
      }
      // Also invalidate general payments list and specific payment
      invalidateQueries.payments();
      // Call the caller's onSuccess if provided
      onSuccessOption?.(updatedPayment, variables, context);
    },
    ...restOptions,
  });
}

/**
 * Delete a payment
 * @param {object} options - Mutation options
 */
export function useDeletePayment(options = {}) {
  const { onSuccess: onSuccessOption, ...restOptions } = options;
  return useMutation({
    mutationFn: api.payments.delete,
    onSuccess: (data, paymentUuid, context) => {
      // Invalidate payments queries
      invalidateQueries.payments();
      // If we have the student UUID from context, invalidate that too
      if (context?.studentUuid) {
        invalidateQueries.studentPayments(context.studentUuid);
        invalidateQueries.student(context.studentUuid);
      }
      // Call the caller's onSuccess if provided
      onSuccessOption?.(data, paymentUuid, context);
    },
    ...restOptions,
  });
}

/**
 * Get comprehensive financial summary (totals, by type, by method)
 */
export function useFinancialSummary(options = {}) {
  return useQuery({
    queryKey: queryKeys.payments.financialSummary(),
    queryFn: () => api.payments.financialSummary(),
    staleTime: 5 * 60 * 1000,
    ...options,
  });
}

/**
 * Get payment summaries across all active students (totals, collection rate)
 */
export function useStudentPaymentSummaries(options = {}) {
  return useQuery({
    queryKey: queryKeys.payments.studentPaymentSummaries(),
    queryFn: () => api.payments.studentPaymentSummaries(),
    staleTime: 5 * 60 * 1000,
    ...options,
  });
}

/**
 * Preview a Stripe payment CSV upload (no DB writes)
 */
export function useUploadPaymentsPreview(options = {}) {
  return useMutation({
    mutationFn: api.payments.uploadPreview,
    ...options,
  });
}

/**
 * Resolve a flagged payment (assign to student, mark resolved, or mark as error)
 */
export function useResolvePayment(options = {}) {
  const { onSuccess: onSuccessOption, ...restOptions } = options;
  return useMutation({
    mutationFn: ({ uuid, ...data }) => api.payments.resolve(uuid, data),
    onSuccess: (data, variables, context) => {
      invalidateQueries.payments();
      onSuccessOption?.(data, variables, context);
    },
    ...restOptions,
  });
}

/**
 * Commit a previewed payment upload, creating Payment records
 */
export function useUploadPaymentsCommit(options = {}) {
  const { onSuccess: onSuccessOption, ...restOptions } = options;
  return useMutation({
    mutationFn: api.payments.uploadCommit,
    onSuccess: (data, variables, context) => {
      invalidateQueries.payments();
      onSuccessOption?.(data, variables, context);
    },
    ...restOptions,
  });
}

const paymentHooks = {
  useStudentPayments,
  usePayments,
  usePayment,
  useCreatePayment,
  useUpdatePayment,
  useDeletePayment,
  useResolvePayment,
  useUploadPaymentsPreview,
  useUploadPaymentsCommit,
  useFinancialSummary,
  useStudentPaymentSummaries,
};

export default paymentHooks;
